import React, {
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import {
    Box,
    Paper,
    Typography,
    Card,
    Checkbox,
} from "@mui/material";
// Import Component
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { getTenantTranslation } from "../utils/getTenantTranslation";
import { normalizeBoardBasis } from "../utils/boardBasis";

const ListaOrdini = React.memo(function ListaOrdini({
    ordiniDivisi,
    selectedTable,
    ordineSchermo,
    ordiniUniti,
    checkboxSelectedIds,
    onCheck,
    adjustmentItems = [],
}) {

    const { t } = useTranslation();
    const theme = useTheme();
    const tenantType = localStorage.getItem('tenantType');

    const lista = ordiniDivisi ? ordineSchermo : ordiniUniti;
    const getSelectionKey = (row) => {
        if (!row) return "";
        if (ordiniDivisi) return String(row.idSplit || "");
        const type = row?.isFixedPriceMenu ? "fixed-menu" : row?.isPackage ? "pkg" : "prd";
        return `${type}:${Number(row?.id)}`;
    };

    const [scrollTick, setScrollTick] = useState(0);

    const handleScroll = () => {
        setScrollTick(t => t + 1);
    };

    function getVariantIds(orderProduct) {
        if (Array.isArray(orderProduct?.variantIds)) {
            return orderProduct.variantIds
                .map((id) => Number(id))
                .filter((id) => Number.isInteger(id) && id > 0);
        }
        if (Array.isArray(orderProduct?.orderProductVariants)) {
            return orderProduct.orderProductVariants
                .map((opv) => Number(opv?.variantId ?? opv?.variant?.id))
                .filter((id) => Number.isInteger(id) && id > 0);
        }
        const single = Number(orderProduct?.variantId ?? orderProduct?.variant?.id);
        return Number.isInteger(single) && single > 0 ? [single] : [];
    }

    function getVariantNames(orderProduct) {
        if (Array.isArray(orderProduct?.orderProductVariants)) {
            return orderProduct.orderProductVariants
                .map((opv) => opv?.variant?.name)
                .filter(Boolean);
        }
        if (Array.isArray(orderProduct?.variants)) {
            return orderProduct.variants
                .map((v) => v?.name)
                .filter(Boolean);
        }
        const productVariants = Array.isArray(orderProduct?.product?.variants) ? orderProduct.product.variants : [];
        return getVariantIds(orderProduct)
            .map((id) => productVariants.find((v) => Number(v?.id) === Number(id))?.name)
            .filter(Boolean);
    }

    function getOrderProductLabel(orderProduct) {
        const baseName = orderProduct?.product?.name || "";
        const variantNames = getVariantNames(orderProduct);
        if (!variantNames.length) return baseName;
        return `${baseName} + ${variantNames.join(" + ")}`;
    }

    //fa in modo di dare la schermata solo se il tavolo viene selezionato, altrimenti da il messaggio adeguato a schermo
    if (!selectedTable) {
        return (
            <Card sx={{
                ...theme.checkout.card_checkout, ...{
                    width: "100%",
                    height: "100%",
                    alignItems: "center",
                    alignContent: "center",
                    my: 0
                }
            }}>
                <Typography variant="h4" textAlign={"center"}>{getTenantTranslation(t, tenantType, "No_table_selected")}</Typography>
            </Card>
        )
    }

    //Fa in modo di dare la schermata solo se trova ordini attivi nel tavolo
    if (!lista || lista.length === 0 || selectedTable.status === "OCCUPATO") {
        return (
            <Card sx={{
                ...theme.checkout.card_checkout,
                ...{
                    width: "100%",
                    height: "100%",
                    alignItems: "center",
                    alignContent: "center",
                    my: 0
                }
            }}>
                <Typography variant="h4" textAlign={"center"}>{getTenantTranslation(t, tenantType, "checkout_page.noRegisteredOrders")}</Typography>
            </Card>
        )
    }

    return (
        <Box minHeight={"1px"} sx={{ height: "100%", overflowY: "auto" }} onScroll={handleScroll}>
            {lista.map((prodotto) => (
                <ProdottoCard
                    key={getSelectionKey(prodotto)}
                    prodotto={prodotto}
                    productLabel={getOrderProductLabel(prodotto)}
                    selected={checkboxSelectedIds.has(getSelectionKey(prodotto))}
                    onCheck={onCheck}
                    selectionKey={getSelectionKey(prodotto)}
                    scrollTick={scrollTick}
                />
            ))}
            {adjustmentItems.map((item) => (
                <AdjustmentCard key={item.orderId || item.label} item={item} />
            ))}
        </Box>
    );
});

const ProdottoCard = React.memo(function ProdottoCard({ prodotto, productLabel, selected, onCheck, selectionKey, scrollTick }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const id = selectionKey;
    const basePrice = Number(prodotto.product?.price ?? 0);
    const unitPrice = Number(prodotto.unitPrice ?? basePrice);
    const rowDiscount = Number(prodotto.discount ?? 0);
    const quantity = Number(prodotto.quantity || 0);
    const rowTotal = unitPrice * quantity;
    const originalUnitPrice = unitPrice - rowDiscount;
    const rowDiscountPercent = originalUnitPrice !== 0 ? ((rowDiscount * 100) / originalUnitPrice) : 0;
    const quantityLabel = prodotto?.product?.pricePerWeight
        ? `${quantity}${prodotto?.product?.typeOfWeight === "HECTOGRAM" ? "hg" : "kg"}`
        : `${quantity}`;
    const unitPriceLabel = prodotto?.product?.pricePerWeight
        ? `${unitPrice.toFixed(2)}/${prodotto?.product?.typeOfWeight === "HECTOGRAM" ? "hg" : "kg"}`
        : unitPrice.toFixed(2);
    const isIncluded = unitPrice === 0 && Boolean(normalizeBoardBasis(prodotto?.product?.boardBasis));
    const unitPriceDisplay = isIncluded
        ? t("boardBasis.included", { defaultValue: "Included" })
        : `EUR ${unitPriceLabel}`;

    //Per fare in modo che il testo ellipsed scorra in modo automatica
    const textRef = useRef(null);
    const [scroll, setScroll] = useState(false);
    const [duration, setDuration] = useState(6);

    useLayoutEffect(() => {
        if (!textRef.current) return;

        const el = textRef.current;
        setScroll(el.scrollWidth > el.clientWidth);
        if (el.scrollWidth > el.clientWidth) {
            const distance = el.scrollWidth - el.clientWidth;
            setDuration(Math.max(5, distance / 80)); // più è lungo, più dura
        }
    }, [productLabel]);


    return (
        <Box minHeight={"1px"} key={id} flex={1}>
            <Card variant="outlined"
                sx={{
                    ...theme.checkout.card_checkout, ...{
                        minHeight: { xs: "66px", md: "70px", lg: "76px" },
                    }
                }}>
                <Box minHeight={"1px"} display={"flex"} justifyContent={"space-between"} sx={{ alignContent: "center" }}>
                    <Paper
                        sx={
                            {
                                ...{ height: { xs: "42px", md: "46px", lg: "48px" }, width: { xs: "42px", md: "46px", lg: "48px" }, margin: 0.5, alignContent: "center" },
                                ...theme.checkout.paper_quantity
                            }
                        }>
                        <Typography textAlign={"center"} fontWeight={400} fontSize={{ xs: "18px", md: "19px", lg: "20px" }} fontFamily={"Inter"}>{quantityLabel}</Typography>
                    </Paper>
                    <Box width={"100%"}
                        sx={{ flex: 1, margin: 0.5, alignContent: "center", overflow: "hidden", }}>
                        <Typography textAlign={"start"} gutterBottom key={scrollTick} ref={textRef}
                            sx={{
                                ...{
                                    width: "100%",
                                    fontFamily: "Inter",
                                    fontWeight: 500,
                                    fontStyle: "Medium",
                                    fontSize: { xs: "16px", md: "17px", lg: "18px" },
                                    WebkitBoxOrient: "vertical",
                                    textOverflow: "ellipsis",
                                    lineHeight: 1.2,
                                    ...(scroll && {
                                        animation: `marquee ${duration}s linear infinite`,
                                        animationDelay: "2s",
                                        "@keyframes marquee": {
                                            "0%": { transform: "translateX(0%)" },
                                            "10%": { transform: "translateX(0%)" },
                                            "90%": { transform: `translateX(-${textRef.current.scrollWidth - textRef.current.clientWidth}px)` },
                                            "100%": { transform: `translateX(-${textRef.current.scrollWidth - textRef.current.clientWidth}px)` },
                                        }
                                    }),
                                },
                                ...theme.checkout.animation_typography
                            }}>{productLabel}</Typography>
                        <Typography
                            sx={{
                                fontFamily: "Inter",
                                fontWeight: 400,
                                fontStyle: "Regular",
                                fontSize: { xs: "14px", md: "15px", lg: "16px" },
                                width: "100%"
                            }}>{quantityLabel} x {unitPriceDisplay}</Typography>
                    </Box>
                    <Box minHeight={"1px"} display="flex" sx={{ margin: 1, alignContent: "center", gap: 1 }}>
                        <Box textAlign={"end"}>
                            <Typography alignContent={"center"}
                                fontFamily={"Inter"}
                                fontWeight={600}
                                fontSize={{ xs: "14px", md: "15px", lg: "16px" }}
                            >{isIncluded ? t("boardBasis.included", { defaultValue: "Included" }) : `EUR ${originalUnitPrice.toFixed(2)}`}</Typography>
                            {rowDiscount ?
                                (<Typography alignContent={"center"}
                                    fontFamily={"Inter"}
                                    fontWeight={600}
                                    fontSize={{ xs: "14px", md: "15px", lg: "16px" }}
                                >{rowDiscount > 0 ? "+" : null}{rowDiscount.toFixed(2)} ({rowDiscountPercent.toFixed(0)}%)
                                </Typography>) : (null)
                            } 
                            <Typography alignContent={"center"}
                                fontFamily={"Inter"}
                                fontWeight={600}
                                fontSize={{ xs: "14px", md: "15px", lg: "16px" }}
                            >EUR {rowTotal.toFixed(2)}</Typography>
                        </Box>
                        <Checkbox
                            disableRipple
                            checked={selected}
                            onChange={() => { onCheck(selectionKey, prodotto) }}
                        />
                    </Box>
                </Box>
            </Card>
        </Box>
    );
});

const AdjustmentCard = React.memo(function AdjustmentCard({ item }) {
    const theme = useTheme();
    const isDiscount = Number(item?.sign || 0) < 0;
    const color = isDiscount
        ? (theme.palette.mode === "light" ? "#a33a2b" : "#ff9b8b")
        : (theme.palette.mode === "light" ? "#125605" : "#78BD6A");

    return (
        <Card variant="outlined" sx={{ ...theme.checkout.card_checkout, minHeight: 58 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25, width: "100%" }}>
                <Typography fontFamily="Inter" fontWeight={600} fontSize={{ xs: "15px", md: "16px" }}>
                    {item?.label}
                </Typography>
                <Typography fontFamily="Inter" fontWeight={700} color={color}>
                    {isDiscount ? "-" : "+"} EUR {Number(item?.amount || 0).toFixed(2)}
                </Typography>
            </Box>
        </Card>
    );
});

export default ListaOrdini;

